import React, { useContext, useEffect, useRef, useState } from 'react';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { MoreVertRounded } from '@mui/icons-material';
import { CircularProgress, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Fade from '@mui/material/Fade';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ModalContext from '../../context/ModalContext';
import PageSearchBar from '../../common/PageSearchBar';
import MapIcon from '../../assets/img/company/location_icon.png';
import './Popins.scss';

export default function Popins() {
  const [companiesData, setCompaniesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayedData, setDisplayedData] = useState([]);
  const [visibleItems, setVisibleItems] = useState(20);
  const [showMenu, setShowMenu] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPopin, setSelectedPopin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const tableContainerRef = useRef(null);

  const modalCtx = useContext(ModalContext);
  const authCtx = useContext(AuthContext);
  const open = Boolean(anchorEl);

  const handleStatus = (status) => {
    const normalized = status || 'draft';
    const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    return <div className={`status-${normalized}`}>{label}</div>;
  };

  const getAllPopins = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/popins`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authCtx.token
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          setCompaniesData(data?.data || []);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllPopins();
  }, []);

  useEffect(() => {
    if (modalCtx.pageUpdate?.page === 'Popins') {
      modalCtx.setPageUpdate({ page: '' });
      getAllPopins();
    }
  }, [modalCtx.pageUpdate]);

  useEffect(() => {
    const filteredData = companiesData
      .map((data) => ({
        ...data,
        popins: (data.popins || []).filter(isLivePopin)
      }))
      .filter((data) => data.popins && data.popins.length > 0)
      .filter((data) => {
        const companyName = (data.companyName || '').toLowerCase();
        return companyName.includes(searchInput.toLowerCase());
      })
      .sort((a, b) => {
        const nameA = a.companyName?.toLowerCase() || '';
        const nameB = b.companyName?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });

    setDisplayedData(filteredData);
    setVisibleItems(20);
  }, [searchInput, loading, companiesData]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
      const scrollTop = tableContainer.scrollTop;
      const scrollHeight = tableContainer.scrollHeight;
      const clientHeight = tableContainer.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setVisibleItems((prev) => (prev < displayedData.length ? prev + 20 : prev));
      }
    };

    tableContainer.addEventListener('scroll', handleScroll);
    return () => {
      tableContainer.removeEventListener('scroll', handleScroll);
    };
  }, [displayedData.length]);

  const handleModal = (modal, data) => {
    handleClose();
    modalCtx.openModal();
    modalCtx.setDetails(modal, data);
  };

  const handleClick = (event) => {
    setAnchorEl(event.target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const popinCountStyle = (count) => (count > 0 ? 'popin-count popin-count-more' : 'popin-count');
  const isLivePopin = (popin) => {
    if (!popin) return false;
    return popin.status === 'active' && Number(popin.scansLeft || 0) > 0;
  };

  const handleResendEmail = async () => {
    if (!selectedPopin?.id) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/popin/${selectedPopin.id}/resend-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        toast.success('Client email queued successfully.');
      } else {
        toast.error(data.error || 'Failed to resend email.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to resend email.');
    } finally {
      handleClose();
    }
  };

  const handleDownloadQr = async () => {
    if (!selectedPopin?.id) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/popin/${selectedPopin.id}/qr`, {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + authCtx.token }
      });
      if (!response.ok) {
        alert('Failed to download QR.');
        handleClose();
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `popin-${selectedPopin.id}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);
      alert('Failed to download QR.');
    } finally {
      handleClose();
    }
  };

  const handleAddCredits = () => {
    if (!selectedPopin?.id) return;
    handleModal('add-credits-popin', { popin: selectedPopin, company: selectedCompany });
  };

  return (
    <>
      <div className="popins-container">
        <div className="header-section">
          <span className="title">POP-INS</span>
          <div className="header-items-div">
            <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" />
          </div>
        </div>

        <TableContainer
          component={Paper}
          className="popin-table"
          ref={tableContainerRef}
          sx={{ height: '100%', overflow: 'auto', overflowY: 'scroll' }}
        >
          <Table sx={{ minWidth: 500, tableLayout: 'fixed' }} stickyHeader>
            <TableHead>
              <TableRow className="company-table-head">
                <TableCell align="left" className="company-head-cell" style={{ width: '50px' }}>&nbsp;</TableCell>
                <TableCell align="left" className="company-head-cell" style={{ width: '170px' }}>Company</TableCell>
                <TableCell align="left" className="company-head-cell" style={{ width: '130px' }}>Scans Left</TableCell>
                <TableCell align="left" className="company-head-cell" style={{ width: '130px' }}>No. of Locations</TableCell>
                <TableCell align="left" className="company-head-cell" style={{ width: '110px' }}>Coupons?</TableCell>
                <TableCell align="left" className="company-head-cell" style={{ width: '140px' }}>Status</TableCell>
                <TableCell align="right" className="company-head-cell" style={{ width: '50px' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="company-table-body">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ border: '0px' }}>
                    <CircularProgress size={52} style={{ color: '#e84118', border: '0px' }} />
                  </TableCell>
                </TableRow>
              ) : (
                displayedData.slice(0, visibleItems).map((data) => (
                  <React.Fragment key={data.advertisingId}>
                    <TableRow className="company-table-row" style={{ backgroundColor: 'white' }}>
                      <TableCell className="company-table-cell" style={{ width: '50px', padding: 0, paddingLeft: '1rem' }} align="left">
                        {data.logo ? <img style={{ height: '32px', width: '32px', borderRadius: '100%' }} src={data.logo} alt="" /> : null}
                      </TableCell>
                      <TableCell className="company-table-cell" style={{ width: '170px' }} align="left">{data.companyName}</TableCell>
                      <TableCell className="company-table-cell" style={{ width: '130px' }} align="left">
                        <div className={popinCountStyle(data.popins.length)}>
                          <img className="popin-count-img" src={MapIcon} alt="" />
                          <span>{data.popins.length} Pop-in{data.popins.length > 1 ? 's' : ''}</span>
                        </div>
                      </TableCell>
                      <TableCell className="company-table-cell" style={{ width: '130px' }} />
                      <TableCell className="company-table-cell" style={{ width: '110px' }} />
                      <TableCell className="company-table-cell" style={{ width: '140px' }} align="left">
                        <div className="status-container">{handleStatus(data.status)}</div>
                      </TableCell>
                      <TableCell className="company-table-cell" style={{ width: '50px' }} align="left">
                        <ArrowDropDownIcon
                          style={{ rotate: showMenu === `${data.advertisingId}hidden` ? '180deg' : '0deg' }}
                          onClick={() => {
                            setShowMenu(showMenu === `${data.advertisingId}hidden` ? null : `${data.advertisingId}hidden`);
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {showMenu === `${data.advertisingId}hidden`
                      ? data.popins.map((item) => (
                          <TableRow key={item.id} className="company-table-row" style={{ backgroundColor: '#fafafc' }}>
                            <TableCell className="company-table-cell" style={{ width: '50px', padding: 0, paddingLeft: '1rem' }} align="left" />
                            <TableCell className="company-table-cell" style={{ width: '170px' }} align="left" />
                            <TableCell className="company-table-cell" style={{ width: '130px' }} align="left">
                              <strong>{item.scansLeft}</strong>
                              {item.totalVisitors ? ` / ${item.totalVisitors}` : ''}
                            </TableCell>
                            <TableCell className="company-table-cell" style={{ width: '130px' }} align="left">{item.locationCount}</TableCell>
                            <TableCell className="company-table-cell" style={{ width: '110px' }} align="left">
                              {item.hasCoupon ? (item.couponCode || 'Yes') : 'No'}
                            </TableCell>
                            <TableCell className="company-table-cell" style={{ width: '140px' }} align="left">
                              <div className="status-container">{handleStatus(item.status)}</div>
                            </TableCell>
                            <TableCell className="company-table-cell" style={{ width: '50px' }} align="left">
                              <MoreVertRounded
                                onClick={(e) => {
                                  setSelectedCompany(data);
                                  setSelectedPopin(item);
                                  handleClick(e);
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      : null}
                  </React.Fragment>
                ))
              )}

              {visibleItems < displayedData.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ border: '0px', padding: '20px' }}>
                    <CircularProgress size={32} style={{ color: '#e84118' }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={() => handleModal('delete-popin', { popin: selectedPopin })}>
          Delete Pop-in
        </MenuItem>
        <MenuItem onClick={handleResendEmail}>Resend client email</MenuItem>
        <MenuItem onClick={handleDownloadQr}>Download QR</MenuItem>
        <MenuItem onClick={handleAddCredits}>Add more credits</MenuItem>
      </Menu>
    </>
  );
}
